import Box from "@mui/material/Box";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { getPublicConfig } from "../../api/config/getPublicConfig";
import { getDossier } from "../../api/dossier/getDossier";
import { setPrimaryQuote } from "../../api/dossier/setPrimaryQuote";
import { getFaqs } from "../../api/faq/getFaqs";
import { getProductTours } from "../../api/productTour/getProductTours";
import { deleteQuote } from "../../api/quote/deleteQuote";
import { getQuote } from "../../api/quote/getQuote";
import { getQuoteDealerPricingRequestCounts } from "../../api/quote/getQuoteDealerPricingRequestCounts";
import { getQuoteVehicleImages } from "../../api/quote/getQuoteVehicleImages";
import { setQuoteLocked } from "../../api/quote/setQuoteLocked";
import { PatchQuotePayload, updateQuote } from "../../api/quote/updateQuote";
import { getVehicleVariantExists } from "../../api/vehicle/getVehicleVariantExists";
import APProductTour from "../../components/APProductTour";
import PageContainer from "../../components/PageContainer";
import {
  isAIVehicleReviewEnabled,
  isAPQuoteHideInvalidCalculationsEnabled,
} from "../../helpers/configuration";
import {
  canRequestDealerPricing,
  isDossierActivated,
} from "../../helpers/dossiers";
import { buildQuoteFilename, downloadFile } from "../../helpers/download";
import { formatQuote } from "../../helpers/format";
import { getDefaultVehiclePricingRequestSearchParams } from "../../helpers/vehiclePricingRequests";
import { useAIConsent } from "../../hooks/useAIConsent";
import { useFaqs } from "../../hooks/useFaqs";
import { useApi } from "../../providers/ApiProvider";
import { useAuth } from "../../providers/AuthProvider";
import { useConfirm } from "../../providers/ConfirmProvider";
import { ErrorCode } from "../../types/Error";
import { QuotePayPeriod } from "../../types/Quote";
import Quote from "../components/Quote";

export type ViewQuoteSubmitAction =
  | "delete"
  | "edit"
  | "personalise"
  | "new"
  | "next"
  | "download"
  | "user"
  | "vehicleSuggestions"
  | "vehicleReview"
  | "calculations"
  | "compareQuotes"
  | "compareFinance"
  | "requestDealerPricing"
  | "selectDealerPricing"
  | "searchDealerPricingRequests";

const ViewQuote = () => {
  const { quoteId } = useParams();
  const [carouselAppLocationId, setCarouselAppLocationId] = useState(
    null as null | string
  );
  const [isPollingFaqs, setIsPollingFaqs] = useState(true);
  const [payPeriodOverride, setPayPeriodOverride] = useState<
    QuotePayPeriod | undefined
  >(undefined);

  const navigate = useNavigate();
  const confirm = useConfirm();
  const { fetchWithAuth } = useApi();
  const { getScopedPageUrl, getScopedPageConfig, inAppScope } = useAuth();
  const { shouldPrompt: shouldPromptForAIConsent } = useAIConsent();
  const productTourContainerRef = useRef<HTMLDivElement>(null);

  if (!quoteId) {
    // TODO: improve client side error messaging
    throw new Error("quote id param is misisng");
  }

  const { mutateAsync, isLoading: isLoadingUpdate } = updateQuote(quoteId);

  const quote = getQuote(quoteId, {
    mutationOptions: {
      errorNavPaths: { [ErrorCode.QuoteDeleted]: getScopedPageUrl("home") },
    },
  });
  const quoteVehicleImages = getQuoteVehicleImages(quoteId);

  const dossierId = quote.data?.identifiers.crmDossierId;
  const dossier = getDossier(dossierId || "", { disabled: !dossierId });

  const vehicleCode = quote.data?.vehicle.variantCode;
  const vehicleVariantExists = getVehicleVariantExists(vehicleCode || "", {
    disabled: !vehicleCode,
  });

  const pricingRequestCounts = getQuoteDealerPricingRequestCounts(quoteId);

  const pageId = "quote-view";
  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId,
    quoteId: quoteId,
    queryOptions: {
      refetchInterval: isPollingFaqs ? 3000 : undefined,
    },
    contextIds: {
      quoteId,
      opportunityId: dossier.data?.opportunity?.id,
    },
  });

  const carouselFaqs = getFaqs(
    { appLocationId: carouselAppLocationId || "", quoteId },
    {
      disabled: !carouselAppLocationId,
    }
  );
  const productTours = getProductTours(pageId, {
    disabled: inAppScope("internal"),
  });
  const configMap = getPublicConfig();

  const {
    mutateAsync: mutatePrimaryToggleAsync,
    isLoading: isLoadingPrimaryToggle,
  } = setPrimaryQuote(dossierId || "", {
    mutationOptions: {
      successAlertMessage: "Your primary quote has been updated",
    },
  });

  const { mutateAsync: mutateQuoteLockedAsync } = setQuoteLocked({
    mutationOptions: {
      successAlertMessage: "Quote locked has been updated",
    },
  });

  const { mutateAsync: mutateDeleteQuoteAsync } = deleteQuote(dossierId || "", {
    mutationOptions: {
      successAlertMessage: "Quote has been deleted",
      successNavPath: getScopedPageUrl("viewDossier", dossierId || ""),
    },
  });

  useEffect(() => {
    if (isSuccessFaqs) {
      const carouselApplocation = faqProps.faqAppLocations.find(
        (location) =>
          location.appLocationIdentifier === "QuoteView-BelowLifeofLeaseSavings"
      );

      if (carouselApplocation) {
        setCarouselAppLocationId(carouselApplocation.id);
      }
    }
  }, [isSuccessFaqs]);

  // Stop polling faqs after 15 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPollingFaqs(false);
    }, 15000); // 15 seconds

    return () => clearTimeout(timer);
  }, []);

  if (quote.isError) {
    throw quote.error;
  }

  if (dossier.isError) {
    throw dossier.error;
  }

  if (configMap.isError) {
    throw configMap.error;
  }
  const receivedPricingRequestCount = useMemo(
    () => pricingRequestCounts.data?.received ?? 0,
    [pricingRequestCounts.data]
  );

  const download = async () => {
    if (quote.isSuccess && dossier.isSuccess) {
      const filename = buildQuoteFilename(
        dossier.data.identity,
        quote.data.vehicle.make,
        quote.data.vehicle.model,
        `${quote.data.term.termMonths}`
      );

      const pdf = await fetchWithAuth<Blob>("getQuotePDF", quoteId, {
        responseOptions: { responseType: "blob" },
      });

      downloadFile(pdf, filename);
    }
  };

  const onDelete = async () => {
    mutateDeleteQuoteAsync(quoteId);
  };

  const onSubmit = async (
    name: string,
    payPeriod: QuotePayPeriod,
    submitAction: ViewQuoteSubmitAction
  ) => {
    if (quote.isSuccess && dossier.isSuccess) {
      const updatePayload: PatchQuotePayload = {};

      if (name !== quote.data.identifiers.name) {
        updatePayload.name = name;
      }

      if (payPeriod !== quote.data.term.payPeriod) {
        updatePayload.payPeriod = payPeriod;
      }

      if (Object.keys(updatePayload).length > 0 && submitAction !== "delete") {
        updatePayload.dossierId = dossierId;
        await mutateAsync(updatePayload);
      }

      switch (submitAction) {
        case "next":
          navigate(getScopedPageUrl("viewDossier", dossier.data.id));
          break;
        case "new":
          navigate(getScopedPageUrl("cloneQuote", quoteId));
          break;
        case "edit":
          navigate(getScopedPageUrl("editQuote", quoteId));
          break;
        case "personalise":
          navigate(getScopedPageUrl("personaliseQuote", quoteId));
          break;
        case "download":
          download();
          break;
        case "delete": {
          const ok = await confirm({
            title: "Delete quote?",
            description: "Are you sure you want to delete this quote?",
          });

          if (ok) {
            onDelete();
          }
          break;
        }
        case "user":
          navigate(
            getScopedPageUrl("viewKeycloakUser", dossier.data.crmContactId)
          );
          break;
        case "vehicleSuggestions":
          navigate(getScopedPageUrl("viewQuoteVehicleSuggestions", quoteId));
          break;
        case "vehicleReview":
          navigate(
            `${getScopedPageUrl(
              "dossierVehicleReview",
              dossierId,
              quote.data.vehicle.variantCode
            )}?hideQuoteButton=true`
          );
          break;
        case "calculations":
          navigate(getScopedPageUrl("viewQuoteCalculations", quoteId));
          break;
        case "compareQuotes":
          navigate(getScopedPageUrl("compareQuotes", dossierId));
          break;
        case "compareFinance":
          navigate(getScopedPageUrl("viewQuoteFinanceComparison", quoteId));
          break;
        case "requestDealerPricing":
          navigate(getScopedPageUrl("requestDealerPricing", quoteId));
          break;
        case "searchDealerPricingRequests": {
          const params = getDefaultVehiclePricingRequestSearchParams(
            quote.data
          );

          navigate(
            `${getScopedPageUrl(
              "searchDealerPricingRequests"
            )}?${params.toString()}`
          );
          break;
        }
        case "selectDealerPricing":
          navigate(getScopedPageUrl("selectDealerPricing", quoteId));
          break;
      }
    }
  };

  const onPayPeriodChange = (period: QuotePayPeriod) => {
    setPayPeriodOverride(period);
  };

  const onSetPrimary = (quoteId: string) => {
    if (dossier.isSuccess) {
      mutatePrimaryToggleAsync({ quoteId });
    }
  };

  const onSetLocked = async (quoteId: string, isLocked: boolean) => {
    if (dossierId) {
      mutateQuoteLockedAsync({ dossierId, quoteId, locked: isLocked });
    }
  };

  const isLoading =
    quote.isFetching ||
    dossier.isFetching ||
    vehicleVariantExists.isFetching ||
    isLoadingFaqs ||
    configMap.isFetching;
  const isSuccess =
    quote.isSuccess &&
    dossier.isSuccess &&
    vehicleVariantExists.isSuccess &&
    isSuccessFaqs &&
    configMap.isSuccess;

  // Can create a quote if there is less then max quotes and the can create flag is true
  const canCreateQuote =
    dossier.isSuccess &&
    (dossier.data.quoteCount || 0) <
      getScopedPageConfig<number>("dossierMaxQuoteCount") &&
    (dossier.data.opportunity?.canCreateQuote || false);

  const isInvalidCalculationsHidden =
    configMap.isSuccess &&
    isAPQuoteHideInvalidCalculationsEnabled(configMap.data);
  const isVehicleSuggestionsEnabled = false;
  const isVehicleReviewEnabled =
    configMap.isSuccess &&
    isAIVehicleReviewEnabled(configMap.data) &&
    !!vehicleVariantExists.data;

  const renderAppLocation = faqProps.renderAppLocation;
  const hideInvalidCalculations =
    isInvalidCalculationsHidden &&
    inAppScope("external") &&
    quote.data?.config.calculationStatus === "invalid";
  const allowSendPricingRequests =
    !!dossier.data &&
    !!quote.data &&
    canRequestDealerPricing(dossier.data, quote.data);
  const allowSelectPricingRequest =
    allowSendPricingRequests && receivedPricingRequestCount > 0;

  return (
    <PageContainer loading={isLoading}>
      {isSuccess && (
        <>
          {!hideInvalidCalculations && (
            <>
              <Box ref={productTourContainerRef}>
                <Quote
                  quote={formatQuote(quote.data, payPeriodOverride)}
                  quoteImages={
                    quoteVehicleImages.isSuccess ? quoteVehicleImages.data : []
                  }
                  contactId={dossier.data.crmContactId}
                  opportunityId={dossier.data.opportunity?.id}
                  faqs={{
                    renderAppLocation,
                    faqAppLocations: faqProps.faqAppLocations,
                    highlightedFaqViews: faqProps.highlightedFaqViews,
                    highlightedAppLocationViews:
                      faqProps.highlightedAppLocationViews,
                    carouselAppLocationId,
                    carouselFaqs: carouselFaqs?.isSuccess
                      ? carouselFaqs.data
                      : [],
                  }}
                  receivedPricingRequestCount={receivedPricingRequestCount}
                  flags={{
                    isPrimary:
                      dossier.data.opportunity?.primaryQuoteId ===
                      quote.data.identifiers.crmQuoteId,
                    canCreateQuote,
                    disablePrimaryToggle:
                      isLoadingPrimaryToggle || dossier.data.hasLockedQuote,
                    shouldPromptForAIConsent: shouldPromptForAIConsent,
                    vehicleSuggestionsEnabled: isVehicleSuggestionsEnabled,
                    vehicleReviewEnabled: isVehicleReviewEnabled,
                    isSubmitting: isLoadingUpdate,
                    isActivatedDossier: isDossierActivated(dossier.data),
                    allowSendPricingRequests: allowSendPricingRequests,
                    allowSelectPricingRequest: allowSelectPricingRequest,
                  }}
                  handlers={{
                    handleSubmit: onSubmit,
                    handleSetPrimary: onSetPrimary,
                    handleSetLocked: onSetLocked,
                    handlePayPeriodChange: onPayPeriodChange,
                  }}
                />
              </Box>
              {productTours.isSuccess && productTours.data[0] && (
                <APProductTour
                  key={productTours.data[0].id}
                  tour={productTours.data[0]}
                  tourContainerRef={productTourContainerRef}
                />
              )}
            </>
          )}
          {hideInvalidCalculations &&
            renderAppLocation(
              "QuoteView-InvalidCalculations",
              <Box></Box>,
              undefined,
              undefined,
              true,
              true
            )}
        </>
      )}
    </PageContainer>
  );
};

export default ViewQuote;
